import { useEffect, useRef, useState } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { useLocation } from "react-router";
import { ICONS } from "../constants/icons";
import { useUser } from "../contexts/UserContext";
import useMoveTo from "../constants/navigation";
import { ref, push, set, update, remove, get } from "firebase/database";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { database, storage } from "../constants/firebase";
import DeleteModal from "../components/DeleteModal";
import MobileHeader from "../components/MobileHeader";
import { convertToWebP, deleteBunnyCollections } from "../utils/utils";

const AddChapter = () => {
    const navigate = useMoveTo();
    const location = useLocation();
    const { currentUser } = useUser();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [form, setForm] = useState({
        title: "",
        coverImage: null,
        description: "",
        status: "Draft"
    });
    const coverInputRef = useRef(null);
    const [isCoverImage, setIsCoverImage] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);


    useEffect(() => {
        if (location.state?.chapter) {
            setForm({
                title: location.state.chapter.title,
                coverImage: null, // Reset cover image for edit
                description: location.state.chapter.description || "",
                status: location.state.chapter.status || "Draft"
            });
            setIsCoverImage(!!location.state.chapter.coverImageUrl);
        }
    }, [])

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({
            ...form,
            [name]: value
        });
    };
    const handleCoverUpload = (e) => {
        setForm({ ...form, coverImage: e.target.files[0] });
    };
    const triggerCoverUpload = () => {
        coverInputRef.current?.click();
    };

    const handleSave = async () => {
        if (!form.title) {
            window.showToast("Please fill all required fields", "error");
            return;
        }

        setIsSubmitting(true);
        try {
            let coverImageUrl = null;

            if (form.coverImage) {
                const webpFile = await convertToWebP(form.coverImage); // ✅ Convert first

                const storageReference = storageRef(
                    storage,
                    `covers/${Date.now()}_${webpFile.name}`
                );
                await uploadBytes(storageReference, webpFile);
                coverImageUrl = await getDownloadURL(storageReference);
            }

            const courseId = location.state?.courseId; // ✅ course comes from navigation state
            if (!courseId) {
                window.showToast("Course not found!", "error");
                return;
            }

            if (location.state?.chapter?.id) {
                // ✅ EDIT chapter
                const updates = {
                    title: form.title,
                    description: form.description,
                    status: form.status,
                    ...(coverImageUrl && { coverImageUrl }),
                };

                await update(
                    ref(database, `Listings/${courseId}/chapters/${location.state.chapter.id}`),
                    updates
                );
                window.showToast("Chapter updated successfully!", "success");
            } else {
                // ✅ ADD new chapter
                const newChapterRef = push(ref(database, `Listings/${courseId}/chapters`));
                const chapterId = newChapterRef.key;

                await set(newChapterRef, {
                    id: chapterId,
                    title: form.title,
                    description: form.description,
                    status: form.status,
                    coverImageUrl: coverImageUrl || "",
                    createdAt: Date.now(),
                });
                window.showToast("Chapter saved successfully!", "success");
                setForm({ title: "", description: "", coverImage: null, status: "Draft" });
            }

        } catch (error) {
            console.error("Error saving chapter:", error);
            window.showToast("Failed to save chapter.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };


    const handleDelete = async () => {
        try {
            setIsDeleting(true);

            const courseId = location.state.courseId;
            const chapterId = location.state.chapter.id;

            // 1️⃣ Fetch all lessons inside this chapter
            const lessonsRef = ref(database, `Listings/${courseId}/chapters/${chapterId}/lessons`);
            const snapshot = await get(lessonsRef);

            if (snapshot.exists()) {
                const lessonsData = snapshot.val();
                const lessonIds = Object.keys(lessonsData);

                // 2️⃣ Loop through each lesson and delete Bunny collection
                for (const lessonId of lessonIds) {
                    try {
                        await deleteBunnyCollections(`lessons_${lessonId}`);
                    } catch (err) {
                        console.warn("⚠️ Failed to delete Bunny video for", lessonId, err);
                    }
                }
            }

            // 3️⃣ Now delete the entire chapter
            await remove(ref(database, `Listings/${courseId}/chapters/${chapterId}`));

            window.showToast("Chapter deleted successfully", "success");

        } catch (err) {
            console.error("Delete failed:", err);
            window.showToast("Failed to delete chapter", "error");
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
        }
    };


    return (
        <div className="h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-y-auto">
            <MobileHeader title={location.state?.chapter ? "Edit Chapter" : "Add Chapter"} isBackBtn={true} />
            <div className="flex">
                <Sidebar />
                <main className="flex-1 h-screen pt-5 pb-24 md:pb-20 px-6 overflow-y-auto bg-white text-black dark:bg-[#1A1A1A] dark:text-white">
                    <div className="flex items-center justify-between">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <img src={ICONS.arrowBack} className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert" alt="Back" />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">{location.state?.chapter ? "Edit Chapter" : "Add Chapter"}</h2>
                        <div />
                    </div>
                    <div className="max-w-md mx-auto md:pt-10 pt-3 space-y-6">
                        {/* Title */}
                        <input
                            type="text"
                            name="title"
                            placeholder="Chapter Title"
                            value={form.title}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] bg-[#F5F9FE] dark:bg-[#F5F9FE] text-black dark:text-black border border-gray-200 dark:border-0"
                        />
                        {/* Cover Image Upload */}
                        <div className="w-full cursor-pointer border-dashed border-2 border-gray-400 p-6 text-center rounded-xl bg-[#F5F9FE] dark:bg-white text-[#7C8BA0]" onClick={triggerCoverUpload}>
                            <input
                                ref={coverInputRef}
                                type="file"
                                accept="image/png,image/jpg,image/jpeg"
                                className="hidden"
                                onChange={handleCoverUpload}
                            />
                            <img src={ICONS.upload} className="mx-auto mb-2 w-8 h-8" alt="Upload" />
                            <p className="font-semibold text-base text-[#7C8BA0]">Upload the Chapter cover image:</p>
                            <p className="text-xs text-[#7C8BA0]">Use 16:9 for best results; other images will be auto-cropped (.png, .jpg, .jpeg).</p>
                            {form.coverImage && (
                                <p className="mt-2 text-xs text-green-600">
                                    Selected: {form.coverImage.name}
                                </p>
                            )}

                            {!form.coverImage && isCoverImage && (
                                <p className="mt-2 text-xs text-green-600">
                                    Cover image already exists
                                </p>
                            )}
                        </div>
                        {/* Explainer Text */}
                        <input
                            type="text"
                            name="description"
                            placeholder="Chapter Description"
                            value={form.description}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] bg-[#F5F9FE] dark:bg-[#F5F9FE] text-black dark:text-black border border-gray-200 dark:border-0"
                        />
                        {/* Status */}
                        <select
                            name="status"
                            value={form.status}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0 appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJsdWNpZGUgbHVjaWRlLWNoZXZyb24tZG93biI+PHBhdGggZD0ibTYgOSA2IDYgNi02Ii8+PC9zdmc+')] bg-no-repeat bg-[position:right_0.5rem_center] bg-[size:1.5em_1.5em]"
                        >
                            <option value="Draft">Draft</option>
                            <option value="Published">Published</option>
                        </select>
                        {/* Save Button */}
                        <button
                            onClick={handleSave}
                            className="bg-[#327691] text-white text-lg font-semibold py-3 rounded-xl w-full mt-4"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Saving..." : "Save Chapter"}
                        </button>
                        {/* Delete Button */}
                        {
                            location.state?.chapter && (
                                <button
                                    onClick={() => setShowDeleteModal(true)}
                                    className="bg-red-600 text-white text-lg font-semibold py-3 rounded-xl w-full mt-2"
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? "Deleting..." : "Delete Chapter"}
                                </button>
                            )
                        }
                    </div>

                    {/* Delete Confirmation Modal */}
                    <DeleteModal
                        isOpen={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={handleDelete}
                        isLoading={isDeleting}
                    />
                </main>
            </div>
        </div>
    );
};

export default AddChapter;
